import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { FaShoppingCart } from "react-icons/fa";
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from "react-i18next";

/* ------------------ STYLES ------------------ */
const CollectionDropdown = styled.div`${tw`relative`}`;
const CollectionMenu = styled.div(({ open, rtl }) => [
  tw`absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`,
  open ? tw`block` : tw`hidden`,
  rtl ? tw`right-0 left-auto` : tw`left-0 right-auto`,
]);

const Header = tw.header`
  w-full flex justify-between items-center mx-auto mt-[10px] px-8
`;

const CartIcon = styled(FaShoppingCart)`
  ${tw`text-gray-800 hover:text-[#133b8c] transition duration-300`};
  font-size: 18px;
  margin-left: 8px;
`;

const NavLinks = styled.div`
  ${tw`flex items-center`};
  gap: clamp(1.8rem, 4.5vw, 6.5rem);
`;

const NavLink = tw.a`
  text-sm font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#133b8c] hover:text-[#000]
  whitespace-nowrap
`;

const PrimaryLink = tw(NavLink)`
  lg:mx-0 px-8 py-3 rounded bg-[#133b8c] text-gray-100
  hover:bg-[#133b8c] hover:text-gray-200 border-b-0
`;

const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};
  img { width: 130px; height: auto; ${tw`mr-3`} }
`;

const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between lg:hidden`;
const NavToggle = tw.button`lg:hidden z-20 focus:outline-none hover:text-primary-500 transition duration-300`;

const MobileNavLinks = motion(styled.div`
  position: absolute;
  top: 170px;
  right: 0;
  width: 16rem;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
`);

const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;

const DesktopNavLinks = tw.nav`hidden lg:flex items-center justify-center w-full`;

const DropdownMenu = styled.div`
  ${tw`absolute mt-1 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5`}
  z-index: 100;
  left: ${({ lang }) => (lang === "ar" ? "20%" : "90%")};
  transform: ${({ lang }) => (lang === "ar" ? "translateX(-30%)" : "translateX(-80%)")};
  min-width: 240px;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.15);
`;

const NavLinkStyled = styled(Link)`
  ${tw`block text-gray-800 font-semibold transition-colors duration-200`};
  text-align: ${({ lang }) => (lang === "ar" ? "right" : "left")};
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  padding: 8px 16px;
  margin: 0 8px;
  font-size: 14px;
  &:hover { background-color: #133b8c; color: #fff; }
`;

const LogoutButton = styled.button`
  ${tw`px-6 py-4 text-gray-800 font-semibold text-left w-full transition-colors duration-200`};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  margin: 2px 8px;
  font-size: 14px;
  text-align: ${({ lang }) => (lang === "ar" ? "right" : "left")};
  &:hover { background-color: #133b8c; color: #fff; }
`;

const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#133b8c] text-white font-semibold transition duration-300`};
  &:hover { background-color: #133b8c; color: #ffffff; }
  &:focus { ${tw`outline-none`} }
`;

// Fallback safety check for breakpoint maps
const collapseBreakPointCssMap = {
  sm: { mobileNavLinks: tw`sm:hidden`, desktopNavLinks: tw`sm:flex`, mobileNavLinksContainer: tw`sm:hidden` },
  md: { mobileNavLinks: tw`md:hidden`, desktopNavLinks: tw`md:flex`, mobileNavLinksContainer: tw`md:hidden` },
  lg: { mobileNavLinks: tw`lg:hidden`, desktopNavLinks: tw`lg:flex`, mobileNavLinksContainer: tw`lg:hidden` },
  xl: { mobileNavLinks: tw`lg:hidden`, desktopNavLinks: tw`lg:flex`, mobileNavLinksContainer: tw`lg:hidden` },
};

/* ------------------ COMPONENT ------------------ */
export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // State variables for toggling submenus
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [mobileCollectionOpen, setMobileCollectionOpen] = useState(false);
  
  const { showNavLinks, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass] || collapseBreakPointCssMap.lg;
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (window.innerWidth >= 1024) {  
        if (!e.target.closest(".collection-dropdown")) {
          setCollectionOpen(false);
        }
      }
      if (!e.target.closest(".user-dropdown")) setDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate("/");
  };

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="logo" />
    </LogoLink>
  );
  logoLink = logoLink || defaultLogoLink;

  // Desktop links config
  links = [
    <NavLinks key="row">
      {logoLink}
      <NavLink href="/">{t("Accueil")}</NavLink>

      <CollectionDropdown className="collection-dropdown">
        {/* FIX 1: Changed from mobileCollectionOpen to collectionOpen for PC view */}
        <NavLink
          as="div"
          style={{ cursor: "pointer" }}
          onClick={() => setCollectionOpen(prev => !prev)}
        >
          {t("Notre_Collection")}
        </NavLink>

        <CollectionMenu open={collectionOpen}>
          <NavLinkStyled to="/cadeaux">{t("categories_menu.Cadeaux")}</NavLinkStyled>
          <NavLinkStyled to="/librairie-papeterie">{t("categories_menu.Librairie_Papeterie")}</NavLinkStyled>
          <NavLinkStyled to="/electromenager">{t("categories_menu.Electromenager")}</NavLinkStyled>
          <NavLinkStyled to="/accessoires-telephone">{t("categories_menu.Accessoires_Telephone")}</NavLinkStyled>
          <NavLinkStyled to="/montres">{t("categories_menu.Montres")}</NavLinkStyled>
          <NavLinkStyled to="/cosmetiques-complements">{t("categories_menu.Cosmetiques_Complements")}</NavLinkStyled>
          <NavLinkStyled to="/parfums">{t("categories_menu.Parfums")}</NavLinkStyled>
          <NavLinkStyled to="/chaussures">{t("categories_menu.Chaussures")}</NavLinkStyled>
          <NavLinkStyled to="/textile-habillement">{t("categories_menu.Textile_Habillement")}</NavLinkStyled>
          <NavLinkStyled to="/bijoux">{t("categories_menu.Bijoux")}</NavLinkStyled>
          <NavLinkStyled to="/materiaux-construction">{t("categories_menu.Materiaux_Construction")}</NavLinkStyled>
          <NavLinkStyled to="/produits-naturels">{t("categories_menu.Produits_Naturels")}</NavLinkStyled>
        </CollectionMenu>
      </CollectionDropdown>

      <NavLink href="/à-props">{t("à_propos")}</NavLink>
      <NavLink href="/contact">{t("Contact")}</NavLink>
      <NavLink href="/checkout" style={{ display: "flex", alignItems: "center" }}>
        <CartIcon />
      </NavLink>

      {!user ? (
        <>
          <NavLink href="/login">{t("Connexion")}</NavLink>
          <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="/signup">
            {t("Inscription")}
          </PrimaryLink>
        </>
      ) : (
        <div className="user-dropdown" tw="relative inline-block">
          <UserButton
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {user.name}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu role="menu" lang={i18n.language}>
              <NavLinkStyled to="/checkout" lang={i18n.language}>{t("Paiement")}</NavLinkStyled>
              <NavLinkStyled to="/ClientChat" lang={i18n.language}>{t("Support_Client")}</NavLinkStyled>
              <NavLinkStyled to="/dashboard" lang={i18n.language}>{t("Tableau_de_bord")}</NavLinkStyled>
              <NavLinkStyled to="/profile" lang={i18n.language}>{t("Profile")}</NavLinkStyled>
              <NavLinkStyled to="/point" lang={i18n.language}>{t("point")}</NavLinkStyled>
              <LogoutButton onClick={handleLogout} lang={i18n.language}>{t("Déconnexion")}</LogoutButton>
            </DropdownMenu>
          )}
        </div>
      )}
    </NavLinks>
  ];

  return (
    <Header className={className || "header-light"}>
      {/* Desktop Navigation */}
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>{links}</DesktopNavLinks>

      {/* Mobile Navigation */}
      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <div className="relative">
          <NavToggle onClick={toggleNavbar}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
          </NavToggle>
          <AnimatePresence>
            {showNavLinks && (
              <MobileNavLinks
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <MobileNavLinksList>
                  <NavLink href="/">{t("Accueil")}</NavLink>
                  <div>
                    {/* FIX 2: Changed from setCollectionOpen to setMobileCollectionOpen for Mobile view */}
                    <NavLink as="div" style={{ cursor: "pointer" }} onClick={() => setMobileCollectionOpen((prev) => !prev)}>
                      {t("Notre_Collection")}
                    </NavLink>
                    {mobileCollectionOpen && (
                      <CollectionMenu open={mobileCollectionOpen} style={{
                          position: "relative",
                          boxShadow: "none",
                          background: "transparent",
                          marginTop: "0.5rem",
                        }}
                      >
                        <NavLinkStyled to="/cadeaux">{t("categories_menu.Cadeaux")}</NavLinkStyled>
                        <NavLinkStyled to="/librairie-papeterie">{t("categories_menu.Librairie_Papeterie")}</NavLinkStyled>
                        <NavLinkStyled to="/electromenager">{t("categories_menu.Electromenager")}</NavLinkStyled>
                        <NavLinkStyled to="/accessoires-telephone">{t("categories_menu.Accessoires_Telephone")}</NavLinkStyled>
                        <NavLinkStyled to="/montres">{t("categories_menu.Montres")}</NavLinkStyled>
                        <NavLinkStyled to="/cosmetiques-complements">{t("categories_menu.Cosmetiques_Complements")}</NavLinkStyled>
                        <NavLinkStyled to="/parfums">{t("categories_menu.Parfums")}</NavLinkStyled>
                        <NavLinkStyled to="/chaussures">{t("categories_menu.Chaussures")}</NavLinkStyled>
                        <NavLinkStyled to="/textile-habillement">{t("categories_menu.Textile_Habillement")}</NavLinkStyled>
                        <NavLinkStyled to="/bijoux">{t("categories_menu.Bijoux")}</NavLinkStyled>
                        <NavLinkStyled to="/materiaux-construction">{t("categories_menu.Materiaux_Construction")}</NavLinkStyled>
                        <NavLinkStyled to="/produits-naturels">{t("categories_menu.Produits_Naturels")}</NavLinkStyled>
                      </CollectionMenu>
                    )}
                  </div>
                  <NavLink href="/à-props">{t("à_propos")}</NavLink>
                  <NavLink href="/contact">{t("Contact")}</NavLink>
                  <NavLink href="/checkout" style={{ display: "flex", alignItems: "center" }}>
                    <CartIcon />
                  </NavLink>

                  {!user ? (
                    <>
                      <NavLink href="/login">{t("Connexion")}</NavLink>
                      <PrimaryLink href="/signup">{t("Inscription")}</PrimaryLink>
                    </>
                  ) : (
                    <>
                      <NavLink href="/checkout">{t("Paiement")}</NavLink>
                      <NavLink href="/dashboard">{t("Tableau_de_bord")}</NavLink>
                      <NavLink href="/profile">{t("Profile")}</NavLink>
                      <NavLink href="/" onClick={handleLogout}>{t("Déconnexion")}</NavLink>
                    </>
                  )}
                </MobileNavLinksList>
              </MobileNavLinks>
            )}
          </AnimatePresence>
        </div>
      </MobileNavLinksContainer>
    </Header>
  );
};